import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useRef, useEffect, useState } from 'react';
import crosshair from 'chartjs-plugin-crosshair';

import './Charts.css';
const legendMargin = {
  id : 'legendMargin',
  beforeInit: function(chart, legend, options) {
    const fitValue = chart.legend.fit;
    chart.legend.fit = function fit() {
      fitValue.bind(chart.legend)();
      return this.height += 16;
    };
  }
};
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  legendMargin
);

const LineChart = ({
  data,
  title = '',
  height = '300px',
  width = '100%',
  showLegend = true,
  showGrid = true,
  responsive = true,
  maintainAspectRatio = false,
  className = ''
}) => {
  const chartRef = useRef(null);
  const [cssColors, setCssColors] = useState({
    error: '#F96767',
    gray600: '#718096'
  });

  // CSS 변수에서 색상 값 가져오기
  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };

    setCssColors({
      error: getCSSVariable('--error'),
      gray600: getCSSVariable('--gray-600')
    });
  }, []);

  // x=0 위치의 그리드 라인 색상을 지정하는 함수
  const getXGridColor = (context) => {
    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600; // x=0 위치의 선 색상
    }
    return 'transparent'; // 다른 그리드 라인은 투명
  };
  // y=0 위치의 그리드 라인 색상을 지정하는 함수
  const getYGridColor = (context) => {
    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600; // y=0 위치의 선 색상
    }
    return 'transparent'; // 다른 그리드 라인은 투명
  };
  
  const chartData = {
    labels: data?.labels || [],
    datasets: data?.datasets || []
  };
  
  const options = {
    responsive,
    maintainAspectRatio,

    scales: {
      x: {
        display: true,
        title: {
          display: true,
          color: cssColors.gray600,
          font: {
            size: 12
          }
        },
        grid: {
          display: showGrid,
          color: getXGridColor,
          drawBorder: false
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          maxTicksLimit: 24,  // 24시간 표시
          callback: function(value, index, values) {
            // 2시간마다만 라벨 표시 (00:00, 02:00, 04:00...)
            if (index % 4 === 0) {
              return this.getLabelForValue(value);
            }
            return '';
          }
        },

      },
      y: {
        display: true,
        beginAtZero: true,
        min: 0,
        title: {
          display: true,
          color: cssColors.gray600,
          font: {
            size: 12
          }
        },
        grid: {
          display: showGrid,
          color: getYGridColor,
          drawBorder: false
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          callback: function(value) {
            return value + ' kWh';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          boxHeight: 5,
          boxWidth: 5,
          font: {
            size: 12,
            weight: '700'
          },
          color: cssColors.gray600
        },
      },
      
      crosshair: {
        line: {
          color: '#f44336',
          width: 2,
        },
        sync: {
          enabled: true,
          group: 1,
          suppressTooltips: false
        },
        zoom: {
          enabled: false,
        },
        snap: {
          enabled: true
        }
      }

    },
    hover: {
      mode: 'index',
      intersect: false,
      animationDuration: 0
    },
    animation: {
      duration: 0.2
    },
    responsiveAnimationDuration: 0,
    elements: {
      point: {
        radius: 1,
        hoverRadius: 1,
      },
      line: {
        tension: 0,  // 부드러운 곡선 제거, 직선으로 연결
      }
    }
  };


  return (
    <div 
      className={`chart-wrapper line-chart ${className}`}
      style={{ height, width }}
    >
      <Line 
        ref={chartRef}
        data={chartData} 
        options={options} 
        plugins={[crosshair]}
      />
    </div>
  );
};

export default LineChart;
